import type { ReactNode } from "react";
import type { SocketStatus } from "../hooks";
import type { State } from "../store";
import { num } from "../format";

/* The panel for a narrow window: an OBS custom dock, a popout on a second
   monitor, a strip beside the game.

   Nothing in here is a second implementation. The composer, the soundboard and
   the on-air card are the very components the full panel renders, handed in
   already built, so a dock cannot quietly fall behind the panel it docks. What
   is left out is everything you configure rather than use mid-stream: the
   history, the stats, the admin card, the settings. */
export function MiniShell({
  state,
  socketStatus,
  onLock,
  banner,
  composer,
  onAir,
  soundboard,
}: {
  state: State;
  socketStatus: SocketStatus;
  onLock: (locked: boolean) => void;
  banner: ReactNode;
  composer: ReactNode;
  onAir: ReactNode;
  soundboard: ReactNode;
}) {
  const admin = state.me?.admin ?? false;
  // Same reading as the header: remaining when there is a cap, used when not.
  const credits = state.subscription
    ? state.subscription.limit
      ? num(state.subscription.remaining)
      : num(state.subscription.used)
    : "—";

  return (
    <div className="mini-shell">
      <header className="mini-bar">
        <span className="pill">
          <span className={`dot ${socketStatus === "live" ? "on" : "off"}`}></span>
          {socketStatus === "live" ? "live" : socketStatus}
        </span>
        <span className="pill" title="ElevenLabs characters left this billing period">
          credits <b>{credits}</b>
        </span>
        <span className="spacer"></span>
        {admin ? (
          <button
            className={"lock-toggle" + (state.locked ? " on" : "")}
            aria-pressed={state.locked}
            onClick={() => onLock(!state.locked)}
          >
            <span className={`dot ${state.locked ? "off" : "on"}`}></span>
            {state.locked ? "Locked" : "Lock"}
          </button>
        ) : (
          state.locked && <span className="pill outline">TTS locked</span>
        )}
        <a className="ghost mini-full" href="/" target="_blank" rel="noopener">
          Full panel
        </a>
      </header>
      {banner}
      <main className="mini-main">
        {composer}
        {onAir}
        {soundboard}
      </main>
    </div>
  );
}

/* The window it opens in. A named target, so pressing the button twice brings
   the existing popout forward instead of stacking a second one. */
export function openMiniWindow(): void {
  window.open("/mini", "toonspeech-mini", "popup=yes,width=440,height=780");
}
